/**
 * Create Admin User Page
 * Dedicated page for creating admin user accounts with department filtering
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Button, Input, Field, Text } from '@fluentui/react-components';
import {
  PersonRegular,
  LockClosedRegular,
  EyeRegular,
  EyeOffRegular,
  ArrowLeftRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { SearchableDropdown } from '../../components/common';
import { ApiClient, newToast } from '../../configurations/index.config';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '24px',
    display: 'block',
  },
  formContainer: {
    maxWidth: '100%',
  },
  formSection: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldRow: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  fieldHalf: {
    flex: '1 1 50%',
  },
  fieldQuarter: {
    flex: '1 1 25%',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#605e5c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    ':hover': {
      color: '#323130',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
  },
  errorText: {
    color: '#d13438',
    fontSize: '12px',
    marginTop: '4px',
  },
  helperText: {
    color: '#605e5c',
    fontSize: '12px',
    marginTop: '4px',
  },
});

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  department_id?: number;
}

interface Department {
  id: number;
  name: string;
}

interface AdminUser {
  id: string;
  employee_id: string;
}

export const CreateAdminUserFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    employeeId: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    employeeId: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, departmentsRes, usersRes] = await Promise.all([
        ApiClient.get('/get-employee-data'),
        ApiClient.get('/get-departments'),
        ApiClient.get('/get-user-data'),
      ]);

      if (employeesRes.data.status === 'success') {
        setEmployees(employeesRes.data.data || []);
      }
      if (departmentsRes.data.status === 'success') {
        setDepartments(departmentsRes.data.data || []);
      }
      if (usersRes.data.status === 'success') {
        setAdminUsers(usersRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate('/account/admin-users');
  };

  const getFilteredEmployees = useCallback(() => {
    let filtered = employees;

    // Filter out employees who already have admin accounts
    filtered = filtered.filter(emp => !adminUsers.some(user => user.employee_id === emp.id));

    // Filter by department if selected
    if (selectedDepartmentId) {
      filtered = filtered.filter(emp => emp.department_id === Number(selectedDepartmentId));
    }

    return filtered;
  }, [employees, adminUsers, selectedDepartmentId]);

  const validateForm = (): boolean => {
    const newErrors = {
      employeeId: '',
      username: '',
      password: '',
      confirmPassword: '',
    };

    if (!formData.employeeId) {
      newErrors.employeeId = 'Please select an employee';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      newToast('warning', 'Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        employeeID: formData.employeeId,
        username: formData.username,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      const response = await ApiClient.post('/add-new-user', payload);
      if (response.data.status === 'success') {
        newToast('success', 'Admin user created successfully');
        navigate('/account/admin-users');
      } else {
        newToast('error', response.data?.message || 'Failed to create user');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const filteredEmployees = getFilteredEmployees();

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Create Admin User</Text>
        </div>
      </div>

      <Text className={styles.pageSubtitle}>
        Create an admin account for an employee to grant system access
      </Text>

      {/* Form */}
      <ContentCard>
        <div className={styles.formContainer}>
          {/* Employee Selection Section */}
          <div className={styles.formSection}>
            <Text className={styles.sectionTitle}>Employee Information</Text>
            <div className={styles.fieldRow}>
              {/* Department Filter */}
              <div className={styles.fieldHalf}>
                <Field label="Department (Filter)">
                  <SearchableDropdown
                    placeholder="All departments"
                    options={[
                      { id: '', label: 'All departments' },
                      ...departments.map(dept => ({
                        id: String(dept.id),
                        label: dept.name,
                      })),
                    ]}
                    selectedValue={selectedDepartmentId}
                    onSelect={value => setSelectedDepartmentId(value)}
                  />
                </Field>
              </div>

              {/* Employee Selection */}
              <div className={styles.fieldHalf}>
                <Field
                  label="Select Employee"
                  required
                  validationMessage={errors.employeeId}
                  validationState={errors.employeeId ? 'error' : 'none'}
                >
                  <SearchableDropdown
                    placeholder="Search employee..."
                    options={
                      filteredEmployees.length === 0
                        ? [{ id: '', label: 'No available employees', disabled: true }]
                        : filteredEmployees.map(emp => ({
                            id: emp.id,
                            label: `${emp.first_name} ${emp.last_name} (${emp.email})`,
                          }))
                    }
                    selectedValue={formData.employeeId}
                    onSelect={value => {
                      setFormData({ ...formData, employeeId: value });
                      setErrors({ ...errors, employeeId: '' });
                    }}
                  />
                  {filteredEmployees.length === 0 && (
                    <Text className={styles.helperText}>
                      {selectedDepartmentId
                        ? 'No available employees in this department'
                        : 'All employees already have admin accounts'}
                    </Text>
                  )}
                </Field>
              </div>
            </div>
          </div>

          {/* Account Credentials Section */}
          <div className={styles.formSection}>
            <Text className={styles.sectionTitle}>Account Credentials</Text>
            <div className={styles.fieldRow}>
              {/* Username */}
              <div className={styles.fieldHalf}>
                <Field
                  label="Username"
                  required
                  validationMessage={errors.username}
                  validationState={errors.username ? 'error' : 'none'}
                >
                  <Input
                    value={formData.username}
                    onChange={e => {
                      setFormData({ ...formData, username: e.target.value });
                      setErrors({ ...errors, username: '' });
                    }}
                    placeholder="Enter username"
                    contentBefore={<PersonRegular />}
                  />
                </Field>
              </div>

              {/* Password */}
              <div className={styles.fieldQuarter}>
                <Field
                  label="Password"
                  required
                  validationMessage={errors.password}
                  validationState={errors.password ? 'error' : 'none'}
                  hint="Minimum 6 characters"
                >
                  <div className={styles.passwordWrapper}>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={e => {
                        setFormData({ ...formData, password: e.target.value });
                        setErrors({ ...errors, password: '' });
                      }}
                      placeholder="Enter password"
                      contentBefore={<LockClosedRegular />}
                    />
                    <div
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      role="button"
                      tabIndex={0}
                    >
                      {showPassword ? <EyeOffRegular /> : <EyeRegular />}
                    </div>
                  </div>
                </Field>
              </div>

              {/* Confirm Password */}
              <div className={styles.fieldQuarter}>
                <Field
                  label="Confirm Password"
                  required
                  validationMessage={errors.confirmPassword}
                  validationState={errors.confirmPassword ? 'error' : 'none'}
                >
                  <div className={styles.passwordWrapper}>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={e => {
                        setFormData({ ...formData, confirmPassword: e.target.value });
                        setErrors({ ...errors, confirmPassword: '' });
                      }}
                      placeholder="Confirm password"
                      contentBefore={<LockClosedRegular />}
                    />
                    <div
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      role="button"
                      tabIndex={0}
                    >
                      {showConfirmPassword ? <EyeOffRegular /> : <EyeRegular />}
                    </div>
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <Button appearance="secondary" onClick={handleBack} disabled={loading}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={loading || filteredEmployees.length === 0}
            >
              {loading ? 'Creating...' : 'Create Admin User'}
            </Button>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default CreateAdminUserFluentUI;
